// src/lib/schedule.js — shared constants & pure helpers

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export const ROLES = ['Agent', 'Senior Agent', 'Junior Agent', 'Manager', 'Admin']

export const LOCATION_COLORS = {
  office: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', bar: '#10b981' },
  home:   { bg: 'bg-blue-100 dark:bg-blue-900/40',       text: 'text-blue-700 dark:text-blue-300',       bar: '#60a5fa' },
  off:    { bg: 'bg-muted',                               text: 'text-muted-foreground',                   bar: 'transparent' },
}

// Timeline: 07:00–23:00
const TIMELINE_START = 9 * 60
const TIMELINE_END   = 23 * 60
const TIMELINE_RANGE = TIMELINE_END - TIMELINE_START

export function timeToMinutes(t) {
  if (!t) return 0
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

export function timeToPercent(t) {
  const mins = timeToMinutes(t)
  return Math.max(0, Math.min(100, ((mins - TIMELINE_START) / TIMELINE_RANGE) * 100))
}

/**
 * Normalise a day's schedule to always be an array of slots.
 * Handles both old shape {start,end,location} and new [{start,end,location},...].
 * Returns [] if the day is off.
 */
export function daySlots(agent, day) {
  const raw = agent?.schedule?.[day]
  if (!raw) return []
  // Old single-slot shape
  if (!Array.isArray(raw)) {
    if (!raw.start || raw.location === 'off') return []
    return [raw]
  }
  // New multi-slot shape — filter out empty/off slots
  return raw.filter(s => s.start && s.end && s.location !== 'off')
}

/** Is an agent working at all on a given day? */
export function isWorkingDay(agent, day) {
  return daySlots(agent, day).length > 0
}

/** Total scheduled minutes for an agent on a given day (all slots summed) */
export function dayScheduledMinutes(agent, day) {
  return daySlots(agent, day).reduce((sum, s) => {
    const mins = timeToMinutes(s.end) - timeToMinutes(s.start)
    return sum + (mins > 0 ? mins : 0)
  }, 0)
}

/** Summary counts for agent cards */
export function agentDaySummary(agent) {
  const counts = { office: 0, home: 0, off: 0, split: 0 }
  DAYS.forEach(d => {
    const slots = daySlots(agent, d)
    if (slots.length === 0) { counts.off++ }
    else if (slots.length > 1) { counts.split++ }
    else { counts[slots[0].location] = (counts[slots[0].location] || 0) + 1 }
  })
  return counts
}

/**
 * Is a given agent in the office at a specific hour on a given day?
 * Checks all slots.
 */
export function isInOfficeAt(agent, day, hour) {
  return daySlots(agent, day).some(s => {
    if (s.location !== 'office') return false
    const sh = timeToMinutes(s.start)
    const eh = timeToMinutes(s.end)
    return hour * 60 >= sh && hour * 60 < eh
  })
}

/** Count agents in-office at a given day + hour */
export function countInOfficeAt(agents, day, hour) {
  return agents.filter(a => isInOfficeAt(a, day, hour)).length
}

/** Peak hour { hour, count } for a given day */
export function peakHour(agents, day) {
  const hours = Array.from({ length: 16 }, (_, i) => i + 7) // 07–22
  let best = { hour: 9, count: 0 }
  hours.forEach(h => {
    const count = countInOfficeAt(agents, day, h)
    if (count > best.count) best = { hour: h, count }
  })
  return best
}

export function formatHour(h) {
  return `${String(h).padStart(2, '0')}:00`
}

export function heatColor(count, capacity) {
  if (count === 0) return { bg: 'hsl(var(--muted))', text: 'transparent' }
  const ratio = Math.min(1, count / capacity)
  if (ratio <= 0.25) return { bg: '#d1fae5', text: '#065f46' }
  if (ratio <= 0.5)  return { bg: '#6ee7b7', text: '#064e3b' }
  if (ratio <= 0.75) return { bg: '#10b981', text: '#ecfdf5' }
  if (ratio < 1)     return { bg: '#059669', text: '#ecfdf5' }
  return                    { bg: '#dc2626', text: '#fff' }
}

// ─── Override-aware effective slots ──────────────────────────────────────────
/**
 * Returns the effective slots for an agent on a specific date.
 * If a one-off override exists for that date, it takes priority.
 * Otherwise falls back to the base weekly schedule.
 *
 * @param {object} agent
 * @param {string} date        'YYYY-MM-DD'
 * @param {object} overrideMap { [agentId]: { [date]: override } }
 * @returns {Array<{start,end,location}>}
 */
export function effectiveSlots(agent, date, overrideMap = {}) {
  const override = overrideMap[agent.id]?.[date]
  if (override !== undefined) {
    // override exists — use it (even if slots=[] meaning day off)
    return (override.slots || []).filter(s => s.start && s.end)
  }
  // Fall back to base weekly schedule
  const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(date + 'T12:00:00').getDay()]
  return daySlots(agent, dayName)
}


// ─── History-aware schedule lookup ───────────────────────────────────────────
/**
 * Returns the effective slots for an agent on a specific date,
 * using the schedule that was active at that time (from history snapshots).
 *
 * @param {object} agent
 * @param {string} dateStr        'YYYY-MM-DD'
 * @param {Array}  scheduleHistory  all history rows for this agent, sorted ascending by effective_from
 * @returns {Array<{start,end,location}>}
 */
export function scheduledSlotsOnDate(agent, dateStr, scheduleHistory = [], overrideMap = {}) {
  // One-off override for this exact date wins over everything else
  const override = overrideMap[agent.id]?.[dateStr]
  if (override !== undefined) {
    return (override.slots || []).filter(s => s.start && s.end && s.location !== 'off')
  }

  const agentHistory = scheduleHistory
    .filter(h => h.agent_id === agent.id && h.effective_from <= dateStr)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from)) // most recent first

  const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(dateStr + 'T12:00:00').getDay()]

  if (agentHistory.length > 0) {
    const snap = agentHistory[0].schedule
    const raw  = snap?.[dayName]
    if (!raw) return []
    if (!Array.isArray(raw)) {
      if (!raw.start || raw.location === 'off') return []
      return [raw]
    }
    return raw.filter(s => s.start && s.end && s.location !== 'off')
  }

  // No history found before this date — agent wasn't hired yet, or legacy agent
  // Fall back to current schedule only for today/future (safe), not for past dates
  const _n = new Date()
  const today = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`
  if (dateStr >= today) return daySlots(agent, dayName)
  return []
}
// The schedule that was IN FORCE on a date, for agents who have history — the current
// schedule for those who do not. Stats used effectiveSlots() everywhere (2026-09-06): every
// past day was judged against TODAY's timetable, so an agent whose hours changed had wrong
// attendance for the whole period before the change. scheduledSlotsOnDate() is the honest
// lookup but returns [] for a past day with no snapshot, which would erase the stats of every
// agent hired before snapshots were kept. So: history when it exists, current otherwise.
export function slotsOnDate(agent, dateStr, scheduleHistory = []) {
  const hasHistory = (scheduleHistory || []).some(h => h.agent_id === agent.id && h.effective_from <= dateStr)
  return hasHistory ? scheduledSlotsOnDate(agent, dateStr, scheduleHistory) : effectiveSlots(agent, dateStr)
}

// No working day in the 7 days ending dateStr — a schedule that exists but is empty (audit 2026-11-03:
// three active agents had every weekday blank, so they could never owe a silent or late day while
// every scheduled colleague could). The Stats tab flags them for a manager to fix.
export function hasNoWorkingDays(agent, dateStr, scheduleHistory = []) {
  const d = new Date(dateStr + 'T12:00:00Z')
  for (let i = 0; i < 7; i++) {
    const day = new Date(d.getTime() - i * 86400000).toISOString().slice(0, 10)
    if (slotsOnDate(agent, day, scheduleHistory).length) return false
  }
  return true
}

export function countInOfficeAtDate(agents, date, hour, overrideMap = {}) {
  return agents.filter(a => {
    return effectiveSlots(a, date, overrideMap).some(s => {
      if (s.location !== 'office') return false
      const sh = timeToMinutes(s.start)
      const eh = timeToMinutes(s.end)
      return hour * 60 >= sh && hour * 60 < eh
    })
  }).length
}
